import React, { useState, useMemo, useCallback } from "react";
import { View, ScrollView } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@/context/I18nContext";
import { formatRelativeTime, formatSplitDateTime } from "@/lib/dateFormat";
import { AppText } from "@/components/ui/AppText";
import { Button } from "@/components/ui/Button";
import { Breadcrumb } from "@/components/ui/Breadcrumb";
import { Badge } from "@/components/ui/Badge";
import { TabBarNavigation } from "@/components/ui/TabBarNavigation";
import { PageFooter } from "@/components/ui/PageFooter";
import { SearchInput } from "@/components/ui/SearchInput";
import { EmptyState } from "@/components/ui/EmptyState";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";
import { ROLE_LABELS, ROLE_VARIANTS } from "@/constants";
import { usersApi, type AdminUserDetail, type UserActivityEntry } from "@/lib/api/users";
import { UserModal } from "../components/UserModal";
import { DeactivateUserModal } from "../components/DeactivateUserModal";

// ─── Detail Field ─────────────────────────────────────────────────────────────

interface DetailFieldProps {
  label: string;
  children: React.ReactNode;
}

function DetailField({ label, children }: DetailFieldProps) {
  return (
    <View className="gap-3 items-start">
      <AppText weight="medium" className="text-base text-supporting-text uppercase tracking-wider">
        {label}
      </AppText>
      {children}
    </View>
  );
}

// ─── Absolute Time ────────────────────────────────────────────────────────────

function AbsoluteTime({ date, time }: { date: string; time: string }) {
  return (
    <View className="flex-row items-center gap-1">
      <AppText className="text-sm text-dark-text-20">{date}</AppText>
      <View className="w-1 h-1 rounded-full bg-stroke-30" />
      <AppText className="text-sm text-dark-text-20">{time}</AppText>
    </View>
  );
}

// ─── Activity type config ────────────────────────────────────────────────────

const ACTIVITY_TYPE_CONFIG: Record<
  UserActivityEntry["action"],
  { iconName: string; bgClass: string }
> = {
  approved_application: { iconName: "check", bgClass: "bg-green-40" },
  changed_role: { iconName: "user", bgClass: "bg-purple-50" },
  added_user: { iconName: "user-plus", bgClass: "bg-purple-50" },
  deactivated_user: { iconName: "user-x", bgClass: "bg-error" },
  updated_profile: { iconName: "edit-2", bgClass: "bg-blue-50" },
  logged_in: { iconName: "log-in", bgClass: "bg-stroke-70" },
};

// ─── Activity Log Item ────────────────────────────────────────────────────────

interface ActivityLogItemProps {
  entry: UserActivityEntry;
  isLast: boolean;
  toLabel: string;
  locale: string;
  t: ReturnType<typeof useI18n>["t"];
}

function ActivityLogItem({ entry, isLast, toLabel, locale, t }: ActivityLogItemProps) {
  const config = ACTIVITY_TYPE_CONFIG[entry.action] ?? {
    iconName: "activity",
    bgClass: "bg-stroke-70",
  };

  const { date, time } = formatSplitDateTime(entry.createdAt, locale as any);

  return (
    <View className="flex-row gap-4">
      {/* Timeline column */}
      <View className="items-center w-8">
        <View className={`w-8 h-8 rounded-full items-center justify-center ${config.bgClass}`}>
          <Icon as={Feather} name={config.iconName as any} size={16} className="text-white" />
        </View>
        {!isLast && <View className="flex-1 w-px bg-stroke-30 mt-1" />}
      </View>

      {/* Content */}
      <View className={`flex-1 items-start gap-1 ${!isLast ? "pb-8" : ""}`}>
        {/* Actor + description */}
        <View className="flex-row flex-wrap gap-x-1 items-baseline">
          <AppText weight="semibold" className="text-lg text-dark-text-50">
            {entry.actorName}
          </AppText>
          <AppText className="text-lg text-supporting-text">{entry.description}</AppText>
        </View>

        {/* Role change badges */}
        {entry.fromRole && entry.toRole && (
          <View className="flex-row items-center gap-2 py-1">
            <Badge variant={ROLE_VARIANTS[entry.fromRole]} label={ROLE_LABELS[entry.fromRole]} />
            <AppText className="text-base text-supporting-text">{toLabel}</AppText>
            <Badge variant={ROLE_VARIANTS[entry.toRole]} label={ROLE_LABELS[entry.toRole]} />
          </View>
        )}

        {/* Times */}
        <AppText className="text-lg text-supporting-text">
          {formatRelativeTime(entry.createdAt, locale as any, t)}
        </AppText>
        <View className="flex-row items-center gap-1">
          <AppText className="text-sm text-supporting-text-40">{date}</AppText>
          <View className="w-1 h-1 rounded-full bg-stroke-30" />
          <AppText className="text-sm text-supporting-text-40">{time}</AppText>
        </View>
      </View>
    </View>
  );
}

// ─── Status badge variant map ─────────────────────────────────────────────────

const STATUS_VARIANT_MAP = {
  active: "filled-success",
  inactive: "filled-warning",
  disabled: "filled-error",
} as const;

const STATUS_LABEL_MAP = {
  active: "staff.users.tabs.active",
  inactive: "staff.users.tabs.inactive",
  disabled: "staff.users.tabs.disabled",
} as const;

type DetailTab = "profile" | "activity";

// ─── Main Screen ──────────────────────────────────────────────────────────────

export default function UserDetailScreen() {
  const { t, locale } = useI18n();
  const router = useRouter();
  const { id, tab: tabParam } = useLocalSearchParams<{ id: string; tab?: DetailTab }>();

  const activeTab: DetailTab = tabParam === "activity" ? "activity" : "profile";

  const handleTabChange = useCallback(
    (tab: DetailTab) => {
      router.setParams({ tab });
    },
    [router]
  );

  const [activitySearch, setActivitySearch] = useState("");
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [deactivateModalVisible, setDeactivateModalVisible] = useState(false);

  const { data: user, isLoading } = useQuery<AdminUserDetail>({
    queryKey: ["users", "detail", id],
    queryFn: () => usersApi.getById(id!),
    enabled: !!id,
  });

  const { data: activityData } = useQuery({
    queryKey: ["users", "activityLog", id],
    queryFn: () => usersApi.activityLog(id!),
    enabled: !!id,
  });

  const filteredActivity = useMemo(() => {
    const items = activityData?.items ?? [];
    if (!activitySearch.trim()) return items;
    const q = activitySearch.toLowerCase();
    return items.filter(
      (e) => e.actorName.toLowerCase().includes(q) || e.description.toLowerCase().includes(q)
    );
  }, [activityData, activitySearch]);

  if (isLoading) {
    return (
      <View className="flex-1 bg-stroke-10 items-center justify-center">
        <AppText className="text-sm text-dark-text-15">{t("common.loading")}</AppText>
      </View>
    );
  }

  const lastLoginText = user?.lastActivityAt
    ? t("staff.users.detail.lastLogin", {
        time: formatRelativeTime(user.lastActivityAt, locale, t),
        date: formatSplitDateTime(user.lastActivityAt, locale).date,
      })
    : "";

  return (
    <View className="flex-1 bg-stroke-10">
      <ScrollView className="flex-1 bg-white" contentContainerStyle={{ flexGrow: 1 }}>
        {/* ── Breadcrumb ──────────────────────────────────────── */}
        <View className="px-page-x bg-white py-4 border-b border-stroke-50">
          <Breadcrumb
            segments={[
              {
                labelKey: "staff.users.detail.breadcrumb",
                href: "/(staff)/(dashboard)/users",
              },
              { labelText: user?.name ?? "" },
            ]}
          />
        </View>

        {/* ── Page header ─────────────────────────────────────── */}
        <View className="bg-white border-b border-stroke-50 py-6">
          <View className="px-page-x w-full">
            <View className="w-full max-w-8xl self-center flex-row flex-wrap items-start justify-between gap-4">
              <View className="mr-4">
                <View className="flex-row items-center gap-2 flex-wrap">
                  <AppText
                    weight="semibold"
                    className="text-[29px] leading-[31.9px] tracking-[-0.29px] text-dark-text-50"
                  >
                    {user?.name ?? ""}
                  </AppText>
                  {user?.status && (
                    <Badge
                      variant={STATUS_VARIANT_MAP[user.status]}
                      label={STATUS_LABEL_MAP[user.status]}
                    />
                  )}
                </View>
                {lastLoginText ? (
                  <AppText className="text-base text-supporting-text mt-1">{lastLoginText}</AppText>
                ) : null}
              </View>
              <View className="flex-row items-center gap-4">
                {user?.status !== "disabled" && (
                  <Button
                    label={
                      user?.status === "inactive"
                        ? "staff.users.actions.reactivateUser"
                        : "staff.users.actions.deactivateUser"
                    }
                    variant="tertiary"
                    onPress={() => setDeactivateModalVisible(true)}
                    labelClassName="text-primary"
                  />
                )}
                <Button
                  label="staff.users.detail.editUser"
                  variant="primary"
                  onPress={() => setEditModalVisible(true)}
                />
              </View>
            </View>
          </View>
        </View>

        {/* ── Tab bar ─────────────────────────────────────────── */}
        <TabBarNavigation<DetailTab>
          tabs={[
            { key: "profile", label: t("staff.users.detail.tabs.profile") },
            { key: "activity", label: t("staff.users.detail.tabs.activity") },
          ]}
          activeTab={activeTab}
          onTabPress={handleTabChange}
          containerClassName="px-page-x"
          className="max-w-8xl self-center w-full"
        />

        {/* ── Content ─────────────────────────────────────────── */}
        {activeTab === "profile" && (
          <View className="px-page-x tablet:px-20 py-8">
            <View className="w-full max-w-3xl self-center gap-6">
              {/* Personal Information card */}
              <View className="bg-white border border-stroke-30 rounded-2xl overflow-hidden">
                <View className="bg-stroke-30 px-6 py-5 items-start">
                  <AppText
                    weight="semibold"
                    className="text-2xl text-dark-text-50 tracking-[-0.12px]"
                  >
                    {t("staff.users.detail.personalInfo")}
                  </AppText>
                </View>

                <View className="p-6 gap-0">
                  {/* Row: First Name | Last Name */}
                  <View className="flex-row gap-8 py-4">
                    <View className="flex-1">
                      <DetailField label={t("staff.users.detail.firstName")}>
                        <AppText className="text-lg text-dark-text-50">
                          {user?.firstName ?? "—"}
                        </AppText>
                      </DetailField>
                    </View>
                    <View className="flex-1">
                      <DetailField label={t("staff.users.detail.lastName")}>
                        <AppText className="text-lg text-dark-text-50">
                          {user?.lastName ?? "—"}
                        </AppText>
                      </DetailField>
                    </View>
                  </View>

                  {/* Row: Phone Number | Email Address */}
                  <View className="flex-row gap-8 py-4">
                    <View className="flex-1">
                      <DetailField label={t("staff.users.detail.phoneNumber")}>
                        <AppText className="text-lg text-dark-text-50">
                          {user?.phone ?? "—"}
                        </AppText>
                      </DetailField>
                    </View>
                    <View className="flex-1">
                      <DetailField label={t("staff.users.detail.emailAddress")}>
                        <AppText className="text-lg text-dark-text-50">
                          {user?.email ?? "—"}
                        </AppText>
                      </DetailField>
                    </View>
                  </View>

                  {/* Row: Role */}
                  <View className="py-4">
                    <DetailField label={t("staff.users.detail.role")}>
                      {user?.role && (
                        <View className="items-start mt-1">
                          <Badge
                            variant={ROLE_VARIANTS[user.role]}
                            label={ROLE_LABELS[user.role]}
                          />
                        </View>
                      )}
                    </DetailField>
                  </View>

                  {/* Row: Language Preference */}
                  <View className="py-4">
                    <DetailField label={t("staff.users.detail.languagePreference")}>
                      <AppText className="text-lg text-dark-text-50">
                        {user?.languagePreference ?? "—"}
                      </AppText>
                    </DetailField>
                  </View>
                </View>
              </View>

              {/* Administrative Information card */}
              <View className="bg-white border border-stroke-30 rounded-2xl overflow-hidden">
                <View className="bg-stroke-30 px-6 py-5 items-start">
                  <AppText
                    weight="semibold"
                    className="text-2xl text-dark-text-50 tracking-[-0.12px]"
                  >
                    {t("staff.users.detail.adminInfo")}
                  </AppText>
                </View>

                <View className="p-6 gap-0">
                  {/* Row: Added By */}
                  <View className="py-4">
                    <DetailField label={t("staff.users.detail.addedBy")}>
                      <View className="gap-2 mt-1">
                        <AppText className="text-lg text-dark-text-50">
                          {user?.addedByName ?? "—"}
                        </AppText>
                        {user?.addedByRole && (
                          <View className="items-start">
                            <Badge
                              variant={ROLE_VARIANTS[user.addedByRole]}
                              label={ROLE_LABELS[user.addedByRole]}
                            />
                          </View>
                        )}
                      </View>
                    </DetailField>
                  </View>

                  {/* Row: Date Added | Last Modified */}
                  <View className="flex-row gap-8 py-4">
                    <View className="flex-1">
                      <DetailField label={t("staff.users.detail.dateAdded")}>
                        <AppText className="text-lg text-dark-text-50">
                          {user?.dateAdded ? formatRelativeTime(user.dateAdded, locale, t) : "—"}
                        </AppText>
                        {user?.dateAdded &&
                          (() => {
                            const { date, time } = formatSplitDateTime(user.dateAdded, locale);
                            return <AbsoluteTime date={date} time={time} />;
                          })()}
                      </DetailField>
                    </View>
                    <View className="flex-1">
                      <DetailField label={t("staff.users.detail.lastModified")}>
                        <AppText className="text-lg text-dark-text-50">
                          {user?.lastModified
                            ? formatRelativeTime(user.lastModified, locale, t)
                            : "—"}
                        </AppText>
                        {user?.lastModified &&
                          (() => {
                            const { date, time } = formatSplitDateTime(user.lastModified, locale);
                            return <AbsoluteTime date={date} time={time} />;
                          })()}
                      </DetailField>
                    </View>
                  </View>

                  {/* Row: Last Activity */}
                  <View className="py-4">
                    <DetailField label={t("staff.users.detail.lastActivity")}>
                      <AppText className="text-lg text-dark-text-50">
                        {user?.lastActivityAt
                          ? formatRelativeTime(user.lastActivityAt, locale, t)
                          : "—"}
                      </AppText>
                      {user?.lastActivityAt &&
                        (() => {
                          const { date, time } = formatSplitDateTime(user.lastActivityAt, locale);
                          return <AbsoluteTime date={date} time={time} />;
                        })()}
                    </DetailField>
                  </View>
                </View>
              </View>
            </View>
          </View>
        )}

        {/* ── Activity Log ─────────────────────────────────────── */}
        {activeTab === "activity" && (
          <View className="px-page-x tablet:px-20 py-8">
            <View className="w-full max-w-3xl self-center items-start gap-4">
              <AppText weight="semibold" className="text-2xl text-dark-text-50 tracking-[-0.12px]">
                {t("staff.users.detail.tabs.activity")}
              </AppText>

              <SearchInput
                value={activitySearch}
                onChangeText={setActivitySearch}
                placeholder="common.search"
                containerClassName="w-full mb-0"
              />

              {filteredActivity.length === 0 ? (
                <EmptyState description="staff.users.detail.activity.empty" />
              ) : (
                <View className="mt-2">
                  {filteredActivity.map((entry, index) => (
                    <ActivityLogItem
                      key={entry.id}
                      entry={entry}
                      isLast={index === filteredActivity.length - 1}
                      toLabel={t("staff.users.detail.activity.to")}
                      locale={locale}
                      t={t}
                    />
                  ))}
                </View>
              )}
            </View>
          </View>
        )}

        <PageFooter />
      </ScrollView>

      {user && (
        <UserModal
          visible={editModalVisible}
          onDismiss={() => setEditModalVisible(false)}
          onSuccess={() => setEditModalVisible(false)}
          user={user}
        />
      )}

      {user && (
        <DeactivateUserModal
          visible={deactivateModalVisible}
          userId={user.id}
          userName={user.name}
          isActive={user.status === "active"}
          onDismiss={() => setDeactivateModalVisible(false)}
        />
      )}
    </View>
  );
}
